// The store's own checkout funnel and kept carts (first-party, every visit
// whatever its cookie choice): the cart and checkout as they move, sent
// through the analytics pipe (../analytics track):
//
//   cart_view        the cart opened with something in it
//   begin_checkout   the checkout began (value, items)
//   checkout_step    a checkout step came on screen { step } (aldoo-ra STORE_CHECKOUT_STEP)
//   payment_submit   the payment was sent
//   purchase         the payment went through (value, currency)
//   cart_update      the cart as it is now { items, currency, language, email?, timeZone?, step?, visit? } —
//                    kept by the API as the visitor's open cart (commerce/lib/open-carts),
//                    not as an event; `visit`: the sync as a visit begins, no action of the
//                    visitor (an abandoned cart comes back only when they open or change it)
//
// The API's dashboards read them as the checkout funnel (analytics/lib/checkout-funnel).
import PubSub from "pubsub-js"
import { getValue } from "aldoo-ra/Value"
import { track } from "../analytics"

const CART_KEY = "aldoo-store-cart"
const STEP_DATA_KEY = "purchase-flow-step-data"
const SENT_KEY = "aldoo-cart-sent"
const DEBOUNCE_MS = 1500

const cartLines = () => getValue(CART_KEY) || []
const units = (lines) => lines.reduce((n, line) => n + (line.quantity || 1), 0)
const value = (lines) => Math.round(lines.reduce((n, line) => n + (Number(line.amount) || 0) * (line.quantity || 1), 0) * 100) / 100

// the email typed at the checkout's first step (as soon as it is a whole address), else the one it was passed with
let typedEmail
const checkoutEmail = () => {
  const email = getValue(STEP_DATA_KEY)?.email?.email
  return typeof email === "string" ? email : typedEmail
}
// the browser's time zone ("Europe/Sofia"): when the store writes to the visitor (api: commerce/lib/winback)
const timeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

function sendCart(step, { visit = false } = {}) {
  const lines = cartLines()
  track("CART_UPDATE", {
    items: lines.map((line) => ({ pid: line.pid, name: line.name, quantity: line.quantity || 1, price: Math.round((Number(line.amount) || 0) * 100), ...(line.sku ? { sku: line.sku } : {}) })),
    currency: lines[0]?.currency,
    language: typeof document !== "undefined" ? document.documentElement.lang || undefined : undefined,
    email: checkoutEmail(),
    timeZone: timeZone(),
    step,
    ...(visit ? { visit: true } : {}),
  })
}

/** Follows the store's cart and checkout; returns the unsubscribe. Mounted once (CheckoutHost). */
export function followFunnel() {
  let timer = null
  const later = () => {
    clearTimeout(timer)
    timer = setTimeout(() => sendCart(), DEBOUNCE_MS)
  }
  const tokens = [
    PubSub.subscribe("STORE_CART_CHANGED", later),
    // the single-product cart of the framework changes without STORE_CART_CHANGED
    PubSub.subscribe("Tracking_AddToCart", later),
    // the address kept with the cart once typed, before the account is made
    PubSub.subscribe("STORE_CHECKOUT_EMAIL_TYPED", (_, data) => {
      if (!data?.email || data.email === typedEmail) return
      typedEmail = data.email
      later()
    }),
    PubSub.subscribe("STORE_CART_OPENED", () => {
      const lines = cartLines()
      if (lines.length) track("CART_VIEW", { items: units(lines), value: value(lines) })
    }),
    PubSub.subscribe("Tracking_BeginCheckout", (_, data) => track("BEGIN_CHECKOUT", { items: units(cartLines()), value: data?.value })),
    PubSub.subscribe("STORE_CHECKOUT_STEP", (_, data) => {
      if (!data?.stepId) return
      track("CHECKOUT_STEP", { step: data.stepId })
      // the step reached (and the email, once given) go with the kept cart
      if (data.stepId !== "success") sendCart(data.stepId)
    }),
    PubSub.subscribe("STORE_PAYMENT_SUBMIT", () => track("PAYMENT_SUBMIT", { items: units(cartLines()), value: value(cartLines()) })),
    PubSub.subscribe("Tracking_Purchase", (_, data) => track("PURCHASE", { value: data?.value, currency: data?.currency })),
  ]
  // a cart from before this visit: kept once per visit, so a cart started before the API kept them is there too
  try {
    if (cartLines().length && !sessionStorage.getItem(SENT_KEY)) {
      sessionStorage.setItem(SENT_KEY, "1")
      sendCart(undefined, { visit: true })
    }
  } catch {}
  return () => {
    clearTimeout(timer)
    tokens.forEach((token) => PubSub.unsubscribe(token))
  }
}
